
import { supabase } from '@/integrations/supabase/client';
import { logErrorToast, logSuccessToast } from '@/utils/toastLogger';
import { JobSearchResult } from '@/types/jobSearch';
import { ensureJobExists } from './ensureJobExistsService';
import { JOB_SEARCH_RESULT_FIELDS_SELECT } from '../constants/jobServiceConstants';

/**
 * Saves a job for a user
 */
export const saveJob = async (userId: string, jobData: JobSearchResult): Promise<boolean> => {
  if (!userId) {
    logErrorToast('You must be logged in to save jobs.');
    return false;
  }
  
  console.log('[saveJob service] Attempting to save job:', jobData, 'for user:', userId);
  
  try {
    const jobSearchResultId = jobData.id; // jobData is JobSearchResult, so id is direct
    
    if (!jobSearchResultId) {
      console.error('[saveJob service] No job ID provided');
      logErrorToast('Cannot save job: Missing job ID.');
      return false;
    }

    const confirmedJobId = await ensureJobExists(jobData, userId);
    
    if (!confirmedJobId) {
      console.error('[saveJob service] Failed to ensure job exists in database');
      return false;
    }
    
    const { data, error: insertError } = await supabase
      .from('saved_jobs')
      .insert({ user_id: userId, job_search_result_id: confirmedJobId })
      .select(`
        id,
        user_id,
        job_search_result_id,
        notes,
        created_at,
        updated_at,
        job_search_results:job_search_result_id (
          ${JOB_SEARCH_RESULT_FIELDS_SELECT}
        )
      `)
      .single();

    if (insertError) {
      if (insertError.code === '23505') { 
        logSuccessToast('Job already saved.');
        return true;
      } else {
        console.error('[saveJob service] Error inserting into saved_jobs:', insertError);
        logErrorToast(`Failed to save job: ${insertError.message}`);
        return false;
      }
    } else if (data) {
      console.log('[saveJob service] Successfully saved job:', data);
      logSuccessToast('Job saved successfully!');
      return true;
    }
    
    return false;
  } catch (e: any) {
    console.error('[saveJob service] General error in saveJob:', e);
    if (!(e.message && e.message.includes('Failed to ensure job existence'))) {
      logErrorToast(e.message || 'Failed to save job.');
    }
    return false;
  }
};

/**
 * Unsaves a job for a user
 */
export const unsaveJob = async (userId: string, jobSearchResultId: string): Promise<boolean> => {
  if (!userId) {
    logErrorToast('You must be logged in to unsave jobs.');
    return false;
  }
  
  try {
    const { error: deleteError } = await supabase
      .from('saved_jobs')
      .delete()
      .eq('user_id', userId)
      .eq('job_search_result_id', jobSearchResultId);

    if (deleteError) {
      throw deleteError;
    }
    
    logSuccessToast('Job unsaved successfully!');
    return true;
  } catch (e: any) {
    console.error('Error unsaving job:', e);
    logErrorToast(e.message || 'Failed to unsave job.');
    return false;
  }
};
